import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import qs from 'qs';
import DataTable from '@/components/data-table';
import { ClearAllFiltersButton } from '@/components/data-table-actions';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { axiosClient } from '@/libs/axios-client';
import { FINANCIAL_STATEMENTS_DEFAULT_PARAMS } from '@/shared/financialStatements';
import { removeObjectKeys } from '@/utils/utils';
import { StatementsColumns } from './StatementsColumns';

const hasRemoveQueryKeys = ['limit', 'offset'];

export default function StatementsList() {
  const { t } = useTranslation(['tables', 'forms', 'messages', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });

  const { queryParams, setQueryParams } = useQueryParamsContext();

  // Fetch statements with filters
  const { data, status } = useQuery({
    queryKey: ['financial-statements', queryParams],
    queryFn: async () => {
      const params = {
        page: queryParams.offset || 1,
        limit: queryParams.limit || 20,
      };

      if (queryParams.companyName) {
        params.companyName = queryParams.companyName;
      }
      if (queryParams.year) {
        params.year = Array.isArray(queryParams.year)
          ? queryParams.year[0]
          : queryParams.year;
      }
      if (queryParams.companySize) {
        params.companySize = Array.isArray(queryParams.companySize)
          ? queryParams.companySize[0]
          : queryParams.companySize;
      }
      if (queryParams.status) {
        params.status = Array.isArray(queryParams.status)
          ? queryParams.status[0]
          : queryParams.status;
      }

      const queryString = qs.stringify(params, { skipNulls: true });
      const response = await axiosClient.get(`/financial-statements?${queryString}`);
      return response.data;
    },
  });

  const statements = data?.data || [];
  const total = data?.pagination?.total || 0;

  // Clear all filters
  const [form] = Form.useForm();

  const removeQueryParamsKeys = useMemo(() => {
    return removeObjectKeys(FINANCIAL_STATEMENTS_DEFAULT_PARAMS, hasRemoveQueryKeys);
  }, []);

  const handleFilterReset = useCallback(() => {
    setQueryParams(removeQueryParamsKeys);
    form.resetFields();
  }, [setQueryParams, removeQueryParamsKeys, form]);

  return (
    <DataTable
      total={total}
      data={statements}
      title={fs('allStatements')}
      columns={StatementsColumns()}
      offset={queryParams.offset}
      limit={queryParams.limit}
      onChangePagination={setQueryParams}
      headerChildren={
        <ClearAllFiltersButton
          label={t('clearAllFilters', { ns: 'tables' })}
          onReset={handleFilterReset}
          defaultParams={removeQueryParamsKeys}
        />
      }
      className="h-[calc(100svh_-_320px)]"
      status={status}
    />
  );
}
